import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Check, Filter, Plus, Calendar, CreditCard, User, Box, X, Send, Users, AlertCircle, Info, CheckCircle, RefreshCw, Search, CheckSquare, Square } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useApp } from '../contexts/AppContext';
import { useAuth } from '../App';
import { notificationsAPI, trainersAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import PageHeader from '../components/common/PageHeader';

const NotificationsPage = () => {
    const { t } = useTranslation();
    const { theme, colors } = useTheme();
    const { selectedOrganization, organizations, userId, isSuperAdmin, notifications, notificationsLoading: loading, fetchNotifications } = useApp();
    const { user } = useAuth();

    console.log('[NotificationsPage] Render. isSuperAdmin:', isSuperAdmin ? isSuperAdmin() : 'null', 'userId:', userId);

    const [filter, setFilter] = useState('all'); // all, unread, system, payment, session

    // Create Notification State
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [newNotification, setNewNotification] = useState({
        title: '',
        body: '',
        type: 'info',
    });

    // Target Selection State
    const [targetOrgId, setTargetOrgId] = useState('');
    const [trainers, setTrainers] = useState([]);
    const [selectedTrainers, setSelectedTrainers] = useState([]);
    const [trainersLoading, setTrainersLoading] = useState(false);
    const [isSelectAll, setIsSelectAll] = useState(false);
    const lastFetchedOrgId = React.useRef(null);

    const currentOrg = organizations.find(o => o.name === selectedOrganization);
    // If selectedOrganization is 'all' or invalid, currentOrg is undefined.

    // Reset state when modal opens
    useEffect(() => {
        if (showCreateModal) {
            if (currentOrg) {
                setTargetOrgId(currentOrg.id);
            } else {
                setTargetOrgId(''); // User must select
            }
            // Preserve trainers if they are already loaded for the current org
            // setTrainers([]); // REMOVED: Do not clear trainers to avoid redundant re-fetching and "not found" blink
            setSelectedTrainers([]);
            setNewNotification({ title: '', body: '', type: 'info' });
        }
    }, [showCreateModal, currentOrg]);

    // Fetch trainers when targetOrgId changes
    useEffect(() => {
        const fetchTrainers = async () => {
            if (!targetOrgId) return;

            // Optimization: If we already have trainers for this org, don't re-fetch
            if (targetOrgId === lastFetchedOrgId.current && trainers.length > 0) {
                return;
            }

            setTrainersLoading(true);
            try {
                const res = await trainersAPI.getAll(targetOrgId);
                // Ensure we handle the response structure securely
                const trainerList = res.data?.data || [];
                setTrainers(trainerList);
                lastFetchedOrgId.current = targetOrgId;
            } catch (error) {
                console.error("Failed to fetch trainers", error);
                setTrainers([]);
                lastFetchedOrgId.current = null;
            } finally {
                setTrainersLoading(false);
            }
        };

        if (targetOrgId) {
            fetchTrainers();
        }
    }, [targetOrgId]);

    const markAsRead = async (notification) => {
        // We need an orgId to mark as read.
        // If notification object has organizationId, use it. Else fallback to currentOrg.
        const orgId = notification.organizationId || currentOrg?.id;
        if (!orgId) return;

        try {
            await notificationsAPI.markAsRead(orgId, notification.id, notification.createdAt);
            fetchNotifications();
        } catch (error) {
            console.error('Failed to mark as read', error);
        }
    };

    const handleCreateNotification = async (e) => {
        e.preventDefault();

        if (!targetOrgId) {
            alert("Please select an organization.");
            return;
        }

        setCreateLoading(true);
        try {
            // Use selected trainers or current user if empty (fallback logic from before, but maybe better to require selection?)
            // User requested "Trainer list target".
            const receiversList = selectedTrainers;

            // Correct API signature call: (orgId, data)
            const creatorName = user?.name || user?.email || 'Admin';
            await notificationsAPI.create(targetOrgId, {
                title: newNotification.title,
                body: `${newNotification.body} (Oluşturan: ${creatorName})`,
                type: 'info', // Force type as requested
                receivers: receiversList.length > 0 ? receiversList : [userId],
            });

            setShowCreateModal(false);
            setNewNotification({ title: '', body: '', type: 'info' });
            setSelectedTrainers([]);
            fetchNotifications(); // Refresh list
        } catch (error) {
            console.error('Failed to create notification', error);
            alert('Failed to create notification');
        } finally {
            setCreateLoading(false);
        }
    };

    const toggleTrainer = (id) => {
        setSelectedTrainers(prev =>
            prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (isSelectAll) {
            setSelectedTrainers([]);
        } else {
            setSelectedTrainers(trainers.map(t => t.id));
        }
        setIsSelectAll(!isSelectAll);
    };

    const getIconForType = (type) => {
        switch (type) {
            case 'session_created': return <Calendar className="w-5 h-5 text-blue-500" />;
            case 'payment': return <CreditCard className="w-5 h-5 text-green-500" />;
            case 'new_student': return <User className="w-5 h-5 text-purple-500" />;
            case 'student_package_update': return <Box className="w-5 h-5 text-orange-500" />;
            case 'warning': return <AlertCircle className="w-5 h-5 text-amber-500" />;
            case 'success': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
            case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
            default: return <Info className="w-5 h-5 text-blue-400" />;
        }
    };

    const filteredNotifications = notifications.filter(n => {
        if (filter === 'all') return true;
        if (filter === 'unread') {
            return userId && (!n.readBy || !n.readBy.includes(userId));
        }
        return n.type === filter;
    });

    return (
        <div className={`flex h-full ${colors.bgPrimary} p-3 sm:p-6`}>
            <div className={`flex-1 ${colors.cardBg} backdrop-blur-sm rounded-2xl ${colors.cardBorder} shadow-lg overflow-hidden flex flex-col`}>

                {/* Header Section */}
                <div className={`p-4 sm:p-6 border-b ${colors.tableBorder} ${colors.tableHeader} flex-shrink-0`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <div className="flex items-center space-x-3">
                            <h1 className={`text-2xl font-bold ${colors.textPrimary}`}>{t('notificationsPage.title')}</h1>
                            <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                                <span className="text-xs text-white font-bold">{notifications.length}</span>
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <button
                                onClick={fetchNotifications}
                                disabled={loading}
                                className={`flex items-center space-x-2 px-4 py-2.5 ${colors.btnSecondaryBg} ${colors.btnSecondaryBorder} ${colors.btnSecondaryText} rounded-xl ${colors.btnSecondaryHover} transition-all duration-200 font-semibold shadow-sm hover:shadow-md transform hover:scale-[1.02] disabled:opacity-50`}
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                                <span className="hidden sm:inline">{t('notificationsPage.refresh')}</span>
                            </button>

                            {isSuperAdmin && isSuperAdmin() && (
                                <button
                                    onClick={() => setShowCreateModal(true)}
                                    className="flex items-center space-x-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-md hover:shadow-lg transform hover:scale-[1.02]"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span className="hidden sm:inline">{t('notificationsPage.newNotification')}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Filters — horizontal chip rail on phones, wrapping row on larger screens */}
                    <div className="flex sm:flex-wrap gap-2 overflow-x-auto sm:overflow-visible no-scrollbar -mx-1 px-1 pb-1 sm:pb-0">
                        {['all', 'unread', 'session_created', 'payment', 'new_student', 'student_package_update', 'warning', 'info'].map(f => (
                            <button
                                key={f}
                                onClick={() => setFilter(f)}
                                className={`px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 whitespace-nowrap shrink-0 sm:shrink ${filter === f
                                    ? 'bg-blue-600 text-white shadow-md transform scale-105'
                                    : `${colors.bgSecondary} ${colors.textSecondary} hover:bg-gray-200 dark:hover:bg-slate-700 border ${colors.border}`
                                    }`}
                            >
                                {f === 'all' ? t('notificationsPage.filters.all') :
                                    f === 'unread' ? t('notificationsPage.filters.unread') :
                                        f === 'session_created' ? t('notificationsPage.filters.sessionCreated') :
                                            f === 'payment' ? t('notificationsPage.filters.payment') :
                                                f === 'new_student' ? t('notificationsPage.filters.newStudent') :
                                                    f === 'student_package_update' ? t('notificationsPage.filters.packageUpdate') :
                                                        f === 'warning' ? t('notificationsPage.filters.warning') :
                                                            t('notificationsPage.filters.info')}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Content List */}
                <div className="flex-1 overflow-auto p-4 sm:p-6">
                    {loading ? (
                        <div className="flex justify-center py-20"><LoadingSpinner size="large" /></div>
                    ) : filteredNotifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 text-center">
                            <div className={`w-16 h-16 ${colors.bgSecondary} rounded-full flex items-center justify-center mb-4`}>
                                <Bell className={`w-8 h-8 ${colors.textTertiary}`} />
                            </div>
                            <h3 className={`text-lg font-semibold ${colors.textPrimary}`}>{t('notificationsPage.emptyState.title')}</h3>
                            <p className={`${colors.textSecondary} max-w-xs mt-2`}>
                                {t('notificationsPage.emptyState.description')}
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {filteredNotifications.map(notification => {
                                const isRead = userId && notification.readBy && notification.readBy.includes(userId);
                                return (
                                    <div
                                        key={notification.id}
                                        className={`group relative p-5 rounded-2xl border transition-all duration-200 ${isRead
                                            ? `${colors.bgSecondary} ${colors.border} opacity-75 hover:opacity-100`
                                            : `${theme === 'dark' ? 'bg-slate-800 border-blue-900/30' : 'bg-white border-blue-100'} shadow-sm hover:shadow-md ring-1 ring-transparent hover:ring-blue-500/10`
                                            }`}
                                    >
                                        <div className="flex items-start space-x-4">
                                            <div className={`p-3 rounded-xl flex-shrink-0 ${theme === 'dark' ? 'bg-slate-700/50' : 'bg-slate-50'}`}>
                                                {getIconForType(notification.type)}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start gap-4 mb-1">
                                                    <h3 className={`font-bold text-base sm:text-lg ${colors.textPrimary} leading-snug min-w-0 break-words`}>{notification.title}</h3>
                                                    <span className={`text-xs font-medium px-2.5 py-1 rounded-lg ${theme === 'dark' ? 'bg-slate-900 text-slate-400' : 'bg-slate-100 text-slate-500'} whitespace-nowrap flex-shrink-0`}>
                                                        {new Date(notification.createdAt).toLocaleString(undefined, {
                                                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                                        })}
                                                    </span>
                                                </div>
                                                <p className={`${colors.textSecondary} text-sm leading-relaxed`}>{notification.body}</p>

                                                {!isRead && (
                                                    <div className="mt-3 flex justify-end">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); markAsRead(notification); }}
                                                            className="text-xs font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400 flex items-center px-3 py-1.5 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                                                        >
                                                            <Check className="w-3.5 h-3.5 mr-1.5" /> {t('notificationsPage.markAsRead')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {!isRead && (
                                            <div className="absolute top-5 right-0 w-1.5 h-1.5 rounded-full bg-blue-500 mr-[-3px]"></div>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Create Notification Modal */}
            {showCreateModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className={`w-full max-w-lg ${colors.cardBg} rounded-3xl shadow-2xl flex flex-col max-h-[90vh] scale-100 animate-in zoom-in-95 duration-200 border ${colors.border}`}>
                        <div className={`px-6 py-4 border-b ${colors.border} flex justify-between items-center bg-gradient-to-r from-blue-600/5 to-indigo-600/5 flex-shrink-0`}>
                            <h3 className={`text-xl font-bold ${colors.textPrimary} flex items-center`}>
                                <Send className="w-5 h-5 mr-3 text-blue-600" />
                                {t('notificationsPage.createModal.title')}
                            </h3>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className={`p-2 rounded-full hover:bg-black/5 ${colors.textSecondary} transition-colors`}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                            {/* Organization Selector (if in All mode) */}
                            {!currentOrg && (
                                <div className="space-y-2">
                                    <label className={`text-xs font-bold uppercase tracking-wider ${colors.textSecondary}`}>{t('notificationsPage.createModal.organization')}</label>
                                    <select
                                        value={targetOrgId}
                                        onChange={(e) => setTargetOrgId(e.target.value)}
                                        className={`w-full px-4 py-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none`}
                                    >
                                        <option value="">{t('notificationsPage.createModal.selectOrganization')}</option>
                                        {organizations.filter(o => o.id !== 'all').map(org => (
                                            <option key={org.id} value={org.id}>{org.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="space-y-2">
                                <label className={`text-xs font-bold uppercase tracking-wider ${colors.textSecondary}`}>
                                    {t('notificationsPage.createModal.recipients')} {selectedTrainers.length > 0 && `(${selectedTrainers.length})`}
                                </label>

                                <div className={`border ${colors.inputBorder} rounded-xl overflow-hidden flex flex-col ${theme === 'dark' ? 'bg-slate-950/30' : 'bg-gray-50/50'}`}>
                                    {/* Toolbar */}
                                    <div className={`px-4 py-3 border-b ${colors.border} flex justify-between items-center bg-gray-50/50 dark:bg-slate-800/50`}>
                                        <button
                                            type="button"
                                            onClick={handleSelectAll}
                                            className="text-xs font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 uppercase tracking-wide transition-colors"
                                        >
                                            {isSelectAll ? t('notificationsPage.createModal.deselectAll') : t('notificationsPage.createModal.selectAll')}
                                        </button>
                                        <span className={`text-xs font-medium ${colors.textTertiary}`}>
                                            {trainersLoading ? t('notificationsPage.createModal.loadingTrainers') : `${trainers.length} ${t('notificationsPage.createModal.found')}`}
                                        </span>
                                    </div>

                                    {/* List */}
                                    <div className="h-64 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                                        {trainersLoading ? (
                                            <div className="h-full flex flex-col justify-center items-center py-4 text-center">
                                                <LoadingSpinner size="small" />
                                                <span className={`text-xs mt-2 ${colors.textTertiary}`}>{t('notificationsPage.createModal.loadingTrainers')}</span>
                                            </div>
                                        ) : trainers.length === 0 ? (
                                            <div className="h-full flex flex-col justify-center items-center py-4 text-center">
                                                <Users className={`w-8 h-8 mb-2 ${colors.textTertiary} opacity-50`} />
                                                <p className={`text-sm ${colors.textSecondary}`}>{t('notificationsPage.createModal.noTrainers')}</p>
                                            </div>
                                        ) : (
                                            trainers.map(trainer => {
                                                const isSelected = selectedTrainers.includes(trainer.id);
                                                return (
                                                    <div
                                                        key={trainer.id}
                                                        onClick={() => toggleTrainer(trainer.id)}
                                                        className={`flex items-center p-3 rounded-xl cursor-pointer transition-all duration-200 group ${isSelected
                                                            ? 'bg-blue-600 shadow-md transform scale-[0.99]'
                                                            : 'hover:bg-gray-200 dark:hover:bg-slate-700/50'
                                                            }`}
                                                    >
                                                        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center mr-3 transition-all duration-200 ${isSelected
                                                            ? 'bg-white border-white'
                                                            : `border-gray-400 dark:border-slate-500 group-hover:border-gray-500 dark:group-hover:border-slate-400`
                                                            }`}>
                                                            {isSelected && <Check className="w-3.5 h-3.5 text-blue-600 stroke-[3]" />}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className={`text-sm font-semibold truncate ${isSelected
                                                                ? 'text-white'
                                                                : colors.textPrimary
                                                                }`}>
                                                                {trainer.name || 'Unnamed Trainer'}
                                                            </p>
                                                            <p className={`text-xs truncate ${isSelected
                                                                ? 'text-blue-100'
                                                                : colors.textTertiary
                                                                }`}>
                                                                {trainer.email || 'No email'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className={`text-xs font-bold uppercase tracking-wider ${colors.textSecondary}`}>{t('notificationsPage.createModal.notificationTitle')}</label>
                                <input
                                    type="text"
                                    required
                                    placeholder={t('notificationsPage.createModal.titlePlaceholder')}
                                    value={newNotification.title}
                                    onChange={e => setNewNotification({ ...newNotification, title: e.target.value })}
                                    className={`w-full px-4 py-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none font-medium`}
                                />
                            </div>

                            <div className="space-y-2">
                                <label className={`text-xs font-bold uppercase tracking-wider ${colors.textSecondary}`}>{t('notificationsPage.createModal.message')}</label>
                                <textarea
                                    required
                                    rows="4"
                                    placeholder={t('notificationsPage.createModal.messagePlaceholder')}
                                    value={newNotification.body}
                                    onChange={e => setNewNotification({ ...newNotification, body: e.target.value })}
                                    className={`w-full px-4 py-3 rounded-xl border ${colors.inputBorder} ${colors.inputBg} ${colors.inputText} focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none resize-none`}
                                />
                            </div>
                        </div>

                        <div className={`p-6 border-t ${colors.border} flex justify-end space-x-3 bg-gray-50/50 dark:bg-slate-800/20 flex-shrink-0`}>
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(false)}
                                className={`px-6 py-3 rounded-xl font-semibold ${colors.textSecondary} hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors`}
                            >
                                {t('notificationsPage.createModal.cancel')}
                            </button>
                            <button
                                onClick={handleCreateNotification}
                                disabled={createLoading || !targetOrgId}
                                className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-blue-500/30 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed flex items-center"
                            >
                                {createLoading ? <LoadingSpinner size="small" color="white" /> : (
                                    <>
                                        <Send className="w-4 h-4 mr-2" />
                                        {t('notificationsPage.createModal.send')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationsPage;
